import { httpBatchLink, loggerLink } from "@trpc/client"
import { createTRPCNext } from "@trpc/next"
import { type AppRouter, type UnauthenticatedAppRouter } from "./server/routers/_app"
import transformer from "./transformer"

/**
 * A set of strongly-typed React hooks from your `AppRouter` type signature with `createReactQueryHooks`.
 * @link https://trpc.io/docs/react#3-create-trpc-hooks
 */
export const trpc = createTRPCNext<AppRouter>({
    config(opts) {
        const { ctx } = opts
        /**
         * If you want to use SSR, you need to use the server's full URL
         * @link https://trpc.io/docs/ssr
         */
        return {
            /**
             * @link https://trpc.io/docs/data-transformers
             */
            transformer,
            /**
             * @link https://trpc.io/docs/client/links
             */
            links: [
                // adds pretty logs to your console in development and logs errors in production
                loggerLink({
                    enabled: (opts) =>
                        process.env.NODE_ENV === "development" ||
                        (opts.direction === "down" && opts.result instanceof Error),
                }),
                httpBatchLink({
                    url: `/api/trpc`,
                    maxURLLength: 2083,
                    headers() {
                        if (!ctx?.req?.headers) {
                            return {}
                        }
                        // To use SSR properly, you need to forward client headers to the server
                        // This is so you can pass through things like cookies when we're server-side rendering
                        return {
                            cookie: ctx.req.headers.cookie,
                        }
                    },
                }),
            ],
            /**
             * @link https://tanstack.com/query/v4/docs/react/reference/QueryClient
             */
            // queryClientConfig: { defaultOptions: { queries: { staleTime: 60 } } },
        }
    },
    /**
     * @link https://trpc.io/docs/ssr
     */
    ssr: false,
})

export const unauthenticatedTRPC = createTRPCNext<UnauthenticatedAppRouter>({
    config(opts) {
        const { ctx } = opts
        /**
         * If you want to use SSR, you need to use the server's full URL
         * @link https://trpc.io/docs/ssr
         */
        return {
            /**
             * @link https://trpc.io/docs/data-transformers
             */
            transformer,
            /**
             * @link https://trpc.io/docs/client/links
             */
            links: [
                // adds pretty logs to your console in development and logs errors in production
                loggerLink({
                    enabled: (opts) =>
                        process.env.NODE_ENV === "development" ||
                        (opts.direction === "down" && opts.result instanceof Error),
                }),
                httpBatchLink({
                    url: `/api/trpc/unauthenticated`,
                    maxURLLength: 2083,
                    headers() {
                        if (!ctx?.req?.headers) {
                            return {}
                        }
                        // To use SSR properly, you need to forward client headers to the server
                        // This is so you can pass through things like cookies when we're server-side rendering
                        return {
                            cookie: ctx.req.headers.cookie,
                        }
                    },
                }),
            ],
            /**
             * @link https://tanstack.com/query/v4/docs/react/reference/QueryClient
             */
            // queryClientConfig: { defaultOptions: { queries: { staleTime: 60 } } },
        }
    },
    /**
     * @link https://trpc.io/docs/ssr
     */
    ssr: false,
})
